import React, { useContext, useEffect } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';

import { MapProvider } from '@vis.gl/react-maplibre';
import { useTranslation } from 'react-i18next';

import { MapApp } from '../components/Map';
import { InfoContainer } from './InfoContainer';
import { GeocoderInput } from '../components/GeocoderInput';
import { useResponsive } from '../hooks/useResponsive';
import { BottomSheetInfo } from '../components/BottomSheetInfo';

import { CalcButtonContainer } from './CalcButtonContainer';
import { ExploreContext } from '../context/ExploreContext';
import { MapTypeSelect } from '../components/MapTypeSelect';

export const ExploreContainer = ({
  locationFeature,
  handleLocationFeature,
}) => {
  const { isMobile, isDesktop } = useResponsive();
  const { t } = useTranslation();

  const {
    status,
    reCalcActive,
    fetchGeoDataByBbox,
    handleIsOffset,
    handleValueShown,
    handleOffset,
    setStatus,
    expand,
    noRequest,
    alert,
    setAlert,
  } = useContext(ExploreContext);

  const style = {
    container: {
      display: 'flex',
      width: '100%',
      backgroundColor: '#FFFFFF',
      height: isDesktop ? 'calc( 100vh - 4.29em)' : '100vh',
    },
    mapContainer: {
      display: 'flex',
      position: 'relative',
      width: (() => {
        if (isDesktop) {
          if (noRequest) {
            return 'calc(100vw - 400px)';
          } else {
            return expand ? '65%' : '87%';
          }
        } else {
          return '100%';
        }
      })(),
      height: '100%',
      minWidth: 300,
      minHeight: 'calc( 100vh - 4.29em)',
    },
    infoContainer: {
      display: 'flex',
      backgroundColor: '#FFFFFF',
      paddingY: '1em',
      width: (() => {
        if (isDesktop) {
          if (noRequest) {
            return '400px';
          } else {
            return expand ? '35%' : '13%';
          }
        } else {
          return '100%';
        }
      })(),
      minWidth: expand ? 200 : 100,
    },
    functionContainer: {
      display: 'flex',
      position: 'absolute',
      zIndex: 1000,
      left: '50%',
      transform: 'translateX(-50%)',
      marginTop: isMobile ? '1em' : '2em',
      borderRadius: '1em',
      alignItems: 'center',
    },
    geocoderContainer: {
      display: 'flex',
      height: '100%',
      minWidth: isMobile ? 200 : 300,
      backgroundColor: '#FFFFFF',
      borderRadius: '1em',
    },
    buttonContainer: {
      display: 'flex',
      height: '100%',
    },
  };

  useEffect(() => {
    if (status === 'loading' && alert) {
      setAlert(undefined);
    }
  }, [status]);

  useEffect(() => {
    return () => {
      handleLocationFeature(null);
    };
  }, []);

  return (
    <MapProvider>
      <Box sx={style.container}>
        <Box sx={style.mapContainer}>
          <Box sx={{ display: 'flex', width: '100%' }}>
            <Stack
              sx={style.functionContainer}
              spacing={isMobile ? 1 : 2}
              direction={isMobile ? 'row-reverse' : 'row'}
            >
              <Box sx={style.geocoderContainer}>
                <GeocoderInput
                  locationFeature={locationFeature}
                  setLocationFeature={handleLocationFeature}
                />
              </Box>
              <Tooltip
                title={
                  reCalcActive
                    ? t('explore_page.tooltip_data')
                    : t('explore_page.tooltip_zoom')
                }
                arrow
                placement='bottom-start'
              >
                <Box sx={style.buttonContainer}>
                  <CalcButtonContainer
                    reCalcActive={reCalcActive}
                    fetchData={fetchGeoDataByBbox}
                    setStatus={setStatus}
                    locationFeature={locationFeature}
                    handleLocationFeature={handleLocationFeature}
                  />
                </Box>
              </Tooltip>
            </Stack>
            <MapTypeSelect />
            <MapApp />
          </Box>
          {alert && (
            <Box
              sx={(theme) => ({
                position: 'absolute',
                inset: 0, // top:0; right:0; bottom:0; left:0
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(0,0,0,0.5)', // backdrop dim
                color: '#fff',
                zIndex: theme.zIndex.drawer + 1,
              })}
            >
              <Alert
                sx={{
                  width: isDesktop ? null : '80%',
                }}
                severity='error'
                onClose={() => setAlert(undefined)}
              >
                {alert}
              </Alert>
            </Box>
          )}
          {status === 'loading' && (
            <Box
              sx={(theme) => ({
                position: 'absolute',
                inset: 0, // top:0; right:0; bottom:0; left:0
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(0,0,0,0.5)', // backdrop dim
                color: '#fff',
                zIndex: theme.zIndex.drawer + 1,
              })}
            >
              <CircularProgress color='inherit' />
              <Typography sx={{ marginLeft: '1em' }}>
                {t('explore_page.loading_data_label')}
              </Typography>
            </Box>
          )}
        </Box>
        {isDesktop ? (
          <Box sx={style.infoContainer}>
            <InfoContainer />
          </Box>
        ) : (
          <BottomSheetInfo
            status={status}
            handleIsOffset={handleIsOffset}
            handleValue={handleValueShown}
            handleOffSet={handleOffset}
          >
            <InfoContainer />
          </BottomSheetInfo>
        )}
      </Box>
    </MapProvider>
  );
};
